import { Target, Eye } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import Trust from "../components/sections/Trust";
import TrustedBy from "../components/sections/TrustedBy";
import Manifesto from "../components/sections/Manifesto";
import Testimonials from "../components/sections/Testimonials";
import LeadSection from "../components/sections/LeadSection";
import { VALUES, MILESTONES, IMAGES, COMPANY, MISSION, VISION } from "../lib/site";

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHero
        kicker="About Synergy Solar"
        title="Powering the Tricity with honest, engineering-led solar."
        subtitle="Since 2011, Synergy Solar Inc. has helped thousands of homes and businesses across Chandigarh, Punjab and Himachal switch to clean, self-generated power — with craftsmanship you can trust for 25 years."
        image={IMAGES.worker1}
      />

      {/* Story */}
      <section className="bg-cream py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-6 md:px-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold">Our story</p>
            <h2 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-forest md:text-5xl">
              Built by engineers who believe sunlight should power everything.
            </h2>
            <div className="mt-6 space-y-4 text-lg leading-relaxed text-moss">
              <p>We started with a simple frustration: rooftop solar in India was confusing, over-promised and under-delivered. So we built a company that does the opposite — precise system design, only Tier-1 components, transparent pricing and support that lasts as long as the panels do.</p>
              <p>Today {COMPANY.shortName} is one of the Tricity's most trusted solar installers, with dedicated local crews serving eight cities and every kind of roof — from independent homes to factories and housing societies.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative h-[460px] overflow-hidden rounded-[2rem]">
              <img src={IMAGES.resIndia} alt="Synergy Solar installation" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-forest/10 mix-blend-multiply" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-cream pb-4">
        <div className="mx-auto grid max-w-[1400px] gap-6 px-6 md:grid-cols-2 md:px-12">
          <Reveal>
            <div className="h-full rounded-[2rem] border border-line bg-sand p-8 md:p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest text-cream"><Target className="h-6 w-6" /></span>
              <h3 className="mt-5 font-heading text-2xl font-bold text-forest">Our mission</h3>
              <p className="mt-3 text-lg leading-relaxed text-moss">{MISSION}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-[2rem] border border-forest bg-forest p-8 text-cream md:p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold text-obsidian"><Eye className="h-6 w-6" /></span>
              <h3 className="mt-5 font-heading text-2xl font-bold">Our vision</h3>
              <p className="mt-3 text-lg leading-relaxed text-cream/80">{VISION}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <Trust />
      <TrustedBy />

      {/* Values */}
      <section className="bg-sand py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal className="mb-12 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.25em] text-gold">What we stand for</p>
            <h2 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-forest md:text-5xl">The Synergy promise.</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-line bg-white/60 p-7">
                  <span className="font-mono text-sm text-gold">0{i + 1}</span>
                  <h3 className="mt-3 font-heading text-xl font-bold text-forest">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-moss">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-obsidian py-24 text-cream md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal className="mb-14 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.25em] text-gold">Our journey</p>
            <h2 className="mt-4 font-heading text-4xl font-extrabold tracking-tight md:text-5xl">15 years of sunshine.</h2>
          </Reveal>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {MILESTONES.map((m, i) => (
              <Reveal key={m.year} delay={i * 0.1}>
                <div className="border-t border-white/15 pt-6">
                  <p className="font-heading text-4xl font-extrabold text-gold">{m.year}</p>
                  <p className="mt-3 text-sm leading-relaxed text-cream/70">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Manifesto />
      <Testimonials />
      <LeadSection sourcePage="About Page" />
    </main>
  );
}
